
"use client";
import React, { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  useToast,
  Image,
  Grid,
  Icon,
  Box,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useAppContext } from "hooks";
import { PromptModal } from "components";
import { useGetModelCreation } from "queries";
import { useDeleteImage } from "queries/useDeleteImage";
import { FaTrash } from "react-icons/fa";
import { useRouter } from "next/navigation";
import { useFalForUpscale } from "queries"; // Import the upscale hook


type Generation = {
  sas_url: string;
  file_name: string;
  upscaled: boolean;
  status: string;
  prompt: string;
  nsfw?: boolean | null;
  storage_type?: string;
};


interface ModelGenerationDisplayProps {
  modelId: string;
  refetchTrigger?: (refetch: () => void) => void; // Function to register a refetch trigger
}


export const ModelGenerationDisplay: React.FC<ModelGenerationDisplayProps> = ({
  modelId,
  refetchTrigger,
}) => {
  const { profile } = useAppContext();
  const { data: generationsData, refetch } = useGetModelCreation(8, modelId);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<Generation | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: deleteOpen, onClose: deleteClose } = useDisclosure();
  const toast = useToast();
  const router = useRouter();
  const deleteImage = useDeleteImage();
  const { isOpen: isPromptModalOpen, onOpen: openPromptModal, onClose: closePromptModal } = useDisclosure();
  const upscaleMutation = useFalForUpscale(); // Use the upscale hook
  const [isUpscaling, setIsUpscaling] = useState<boolean>(false); // Track upscale state

  const [updatedGenerations, setUpdatedGenerations] = useState<Generation[]>([]);
  const isMobile = useBreakpointValue({ base: true, md: false });


  // Register the refetch function with the parent
  useEffect(() => {
    if (refetchTrigger) {
      refetchTrigger(refetch); // Provide the refetch function to the parent
    }
  }, [refetch, refetchTrigger]);


  useEffect(() => {
    if (generationsData && (generationsData.pages[0] as any)?.image_generations) {
      const latestGenerations = (generationsData.pages[0] as any)?.image_generations.slice(0, 4);
      setUpdatedGenerations(latestGenerations);
    }
  }, [generationsData]);

  const handleUpscale = async () => {
    if (!selectedImage) return;

    try {
      setIsUpscaling(true);
      await upscaleMutation.mutateAsync({
        input_filename: selectedImage.file_name, // Pass the filename
        model_id: modelId, // Use the current modelId
      });

      toast({
        title: "Image Upscaled",
        description: "Your image has been successfully upscaled.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      onClose();
      refetch(); // Refresh data
    } catch (error) {
      toast({
        title: "Upscale Failed",
        description: "Failed to upscale the image. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsUpscaling(false);
    }
  };



  const handleDelete = () => {
    if (!selectedImage) return;

    deleteImage.mutate(
      { file_name: selectedImage.file_name },
      {
        onSuccess: () => {
          toast({
            title: "Image deleted",
            description: "The selected image has been deleted successfully.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          deleteClose();
          refetch();
        },
        onError: (error: any) => {
          console.error("Error deleting image:", error);
          toast({
            title: "Error",
            description: "There was an error deleting the image.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        },
      }
    );
  };

  return (
    <Flex direction="column" mt="20px" align="left" bg={{ base: "transparent", md: "#050918" }}>
      <Text mt="10px" fontWeight={700} textAlign="left">
        Missing a Tag?{" "}
        <span style={{ textDecoration: "underline", fontWeight: 600 }}>
          <a target="_blank" href="https://discord.gg/CMwtccKthy" rel="noopener noreferrer">
            Join our Discord to suggest it
          </a>
        </span>
      </Text>

      {isMobile ? (
        // Mobile View: Two images per row using Flex
        <Flex direction="column" width="100%" mt="20px" gap={4}>
          {updatedGenerations
            .filter((generation) => generation.status !== "pending" && generation.status !== "failed")
            .reduce((rows: JSX.Element[][], generation, index) => {
              const imageUrl = generation.file_name.startsWith("http")
                ? generation.file_name
                : generation.sas_url;

              const imageComponent = imageUrl ? (
                <Flex key={index} direction="column" position="relative" width="50%">
                  <Flex
                    position="relative"
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="14px"
                    overflow="hidden"
                    w="100%"
                    sx={{
                      aspectRatio: "3/4",
                    }}
                  >
                    <Image
                      src={imageUrl}
                      alt={`Generated by model ${modelId}`}
                      objectFit="cover"
                      width="100%"
                      height="100%"
                      onClick={() => {
                        setSelectedImageUrl(imageUrl);
                        setSelectedImage(generation);
                        openPromptModal()
                      }}
                      _hover={{cursor: "pointer"}}
                    />
                    <Flex
                      position="absolute"
                      top="5px"
                      left="5px"
                      zIndex={2}
                    >
                </Flex>
                    {generation.upscaled && (
                      <Flex
                        position="absolute"
                        top="5px"
                        right="5px"
                        bgColor="black"
                        w="20px"
                        h="14px"
                        justifyContent="center"
                        alignItems="center"
                        border="1px solid yellow"
                        borderRadius="2px"
                      >
                        <Text
                          w="100%"
                          fontSize="10px"
                          fontWeight="bold"
                          color="yellow"
                          textAlign="center"
                        >
                          HD
                        </Text>
                      </Flex>
                    )}
                    <Button
                      variant="unstyled"
                      bg="red"
                      w="28px"
                      h="28px"
                      position="absolute"
                      bottom="5px"
                      right="5px"
                      zIndex={2}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(generation);
                        deleteOpen();
                      }}
                    >
                      <Icon as={FaTrash} boxSize={3} color="white" />
                    </Button>
                  </Flex>
                </Flex>
              ) : null;

              if (index % 2 === 0) {
                rows.push([imageComponent!]);
              } else {
                rows[rows.length - 1].push(imageComponent!);
              }
              return rows;
            }, [])
            .map((row, rowIndex) => (
              <Flex key={rowIndex} width="100%" gap={4}>
                {row}
              </Flex>
            ))}
            {selectedImage && <PromptModal 
                    isOpen={isPromptModalOpen} 
                    onClose={closePromptModal} 
                    isFlux={true} 
                    model_id={modelId} 
                    generationImage={selectedImageUrl || ''} 
                    file_name={selectedImage.file_name} 
                    storage_type={selectedImage.storage_type}
                  />      }   
        </Flex>
      ) : (
        // Desktop View: Grid layout
        <Grid
          width="100%"
          gap={5}
          templateColumns={{
            base: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            xl: "repeat(5, 1fr)",
            "2xl": "repeat(6, 1fr)",
          }}
          mt="20px"
        >
          {updatedGenerations
            .filter((generation) => generation.status !== "pending" && generation.status !== "failed")
            .map((generation, index) => {
              const imageUrl = generation.file_name.startsWith("http")
                ? generation.file_name
                : generation.sas_url;

              return imageUrl ? (
                <Flex key={index} direction="column" position="relative">
                  <Flex
                    position="relative"
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    borderRadius="14px"
                    overflow="hidden"
                    w="100%"
                    sx={{
                      aspectRatio: "3/4",
                    }}
                  >
                    <Image
                      src={imageUrl}
                      alt={`Generated by model ${modelId}`}
                      objectFit="cover"
                      width="100%"
                      height="100%"
                      onClick={() => {
                        setSelectedImageUrl(imageUrl);
                        setSelectedImage(generation);
                        openPromptModal();
                      }}
                    />
                    <Flex
                      position="absolute"
                      top="5px"
                      left="5px"
                      zIndex={2}
                    >
                      </Flex>
                    {generation.upscaled && (
                      <Flex
                        position="absolute"
                        top="5px"
                        right="5px"
                        bgColor="black"
                        w="20px"
                        h="14px"
                        justifyContent="center"
                        alignItems="center"
                        border="1px solid yellow"
                        borderRadius="2px"
                      >
                        <Text
                          w="100%"
                          fontSize="10px"
                          fontWeight="bold"
                          color="yellow"
                          textAlign="center"
                        >
                          HD
                        </Text>
                      </Flex>
                    )}
                    <Button
                      variant="unstyled"
                      bg="red"
                      w="28px"
                      h="28px"
                      position="absolute"
                      bottom="5px"
                      right="5px"
                      zIndex={2}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedImage(generation);
                        deleteOpen();
                      }}
                    >
                      <Icon as={FaTrash} boxSize={3} color="white" />
                    </Button>
                  </Flex>
                </Flex>
              ) : null;
            })}
                 {selectedImage && <PromptModal 
                    isOpen={isPromptModalOpen} 
                    onClose={closePromptModal} 
                    isFlux={true} 
                    model_id={modelId} 
                    generationImage={selectedImageUrl || ''} 
                    file_name={selectedImage.file_name} 
                    storage_type={selectedImage.storage_type}
                  />      }  
            <Flex justifyContent="center" alignItems="top" >
              <Button
                borderRadius="15px"
                bg="transparent"
                border="1px solid white"
                onClick={() => router.push(`/creations/${modelId}`)}
                fontSize={{ base: "12px", md: "16px" }}
                py={{ base: 2, md: 4 }}
                px={{ base: 4, md: 6 }}
                width="100%"
                maxWidth={{ md: "auto" }}
              >
                <Text color="white">Creation History</Text>
              </Button>
            </Flex>
        </Grid>
      )}
       
       {/* Mobile-only Creation History Button */}
       {isMobile && (
        <Flex justifyContent="center" mt="20px">
          <Button
            borderRadius="15px"
            bg="transparent"
            border="1px solid white"
            onClick={() => router.push(`/creations/${modelId}`)}
            fontSize="16px"
            py={4}
            px={6}
            width="100%"
          >
            <Text color="white">Creation History</Text>
          </Button>
        </Flex>
      )}
      

      <Modal size={{ base: "xs", md: "md", lg: "xl" }} isOpen={isDeleteOpen} onClose={deleteClose}>
        <ModalOverlay />
        <ModalContent borderRadius={0} bg="#16181F" border="1px solid white">
          <ModalCloseButton />
          <Flex
            justify="center"
            w="100%"
            p={{ base: 5, md: 10 }}
            px={{ base: "20px", md: "80px" }}
            minH="200px"
            direction="column"
          >
            <Text fontSize={{ base: "20px", md: "28px" }} fontWeight={600}>
              Are you sure you want to delete this image?
            </Text>
            <Flex>
              <Button
                mt="30px"
                bg="white"
                borderRadius="32px"
                color="black"
                onClick={handleDelete}
              >
                Delete Image
              </Button>
            </Flex>
          </Flex>
        </ModalContent>
      </Modal>

      {selectedImageUrl && (
        <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
          <ModalOverlay />
          <ModalContent
            bg="black"
            border="1px solid white"
            borderRadius="12px"
            overflow="hidden"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p="4"
          >
            <ModalCloseButton color="white" />
            <ModalBody
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              p="0"
            >
              {/* Display Selected Image */}
              <Image
                src={selectedImageUrl}
                alt="Selected Image"
                w="100%"
                objectFit="contain"
                borderRadius="8px"
                mb="4"
              />

              {/* Upscale Button */}
              <Button
                bg={
                  selectedImage?.upscaled
                    ? "gray"
                    : "button.500"
                }
                color="white"
                w="100%"
                minH="50px"
                mt="10px"
                p="16px, 30px, 16px, 30px"
                fontSize={{ base: "16px", "2xl": "20px" }}
                onClick={handleUpscale}
                isLoading={isUpscaling}
                loadingText="Upscaling..."
                isDisabled={selectedImage?.upscaled || isUpscaling}
                py="6"
              >
                {selectedImage?.upscaled ? "Upscaled" : "Upscale Image (2 credits)"}
              </Button>


            </ModalBody>
          </ModalContent>
        </Modal>
      )}

    </Flex>
  );
};

export default ModelGenerationDisplay;

